import {gql} from '@apollo/client';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import {TestDefinitionTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {ProcedureTestDefinition} from 'procedures/ProcedureDomainTypes';

import {GetTestDefinitionSearchSummaryQuery} from './__generated/GetTestDefinitionSearchSummaryQuery';

const TEST_DEFINITION_SEARCH_SUMMARY_QUERY = gql`
  query GetTestDefinitionSearchSummaryQuery($id: StrictString!) @skipAudit @engComponent(name: PROCEDURES) {
    testDefinition(id: $id) {
      ...TestDefinitionListingGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('TestDefinitionListing').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Retrieves the search summary of a test definition by its ID from DB.
 */
export async function fetchTestDefinitionSearchSummary(
  testDefinitionId: string
): Promise<ProcedureTestDefinition | null> {
  const result = await doQuerySafe<GetTestDefinitionSearchSummaryQuery>(
    {query: TEST_DEFINITION_SEARCH_SUMMARY_QUERY},
    {variables: {id: testDefinitionId}}
  );

  if (isInaccessible(result.testDefinition)) {
    return null;
  }

  const resultSchema = result.testDefinition.resultSchemaCorrect;
  const testDefinition: ProcedureTestDefinition = {
    ...result.testDefinition,
    resultSchemaName: isInaccessible(resultSchema) ? '(No access)' : resultSchema.name,
  };
  return testDefinition;
}

/**
 * Transforms the TestDefinitionSearchItem's response shape into a {@link TestDefinitionTypedValue}.
 */
export function toTestDefinitionTypedValueFromSearchItem(
  testDefinition: ProcedureTestDefinition
): TestDefinitionTypedValue {
  return {
    type: BaseTypedValueType.TEST_DEFINITION,
    id: testDefinition.id,
    name: testDefinition.name,
  };
}

/**
 * Retrieves a test definition search summary from the DB, and converts the summary into a {@link TestDefinitionTypedValue} format.
 */
export async function toTestDefinitionTypedValueFromId(
  testDefinitionId: string
): Promise<TestDefinitionTypedValue | null> {
  const testDefinition = await fetchTestDefinitionSearchSummary(testDefinitionId);

  if (!testDefinition) {
    return null;
  }

  return toTestDefinitionTypedValueFromSearchItem(testDefinition);
}
